import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Accordion } from "react-bootstrap";
import { FaCheckCircle, FaExclamationTriangle, FaHourglassHalf } from "react-icons/fa";
import Api from "../../utils/Api";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import remove_button from "../../resources/remove.png";
import Notifier from "../../components/Notifier";

// The tasks of a run execute in parallel, so a run lasts from its first start to its last end.
function runDuration(tasks) {
    const start = moment.min(tasks.map((task) => moment(task.startTime)));
    const end = moment.max(tasks.map((task) => moment(task.endTime)));
    const seconds = Math.round(end.diff(start, "seconds", true));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function Automations() {
    const [logs, setLogs] = useState([]);
    const [showDetails, setShowDetails] = useState(false);
    const [currentLogDetails, setCurrentLogDetails] = useState({ details: "" });
    const isMobile = useMediaQuery({ query: "(max-width: 599px)" });
    const notifierRef = React.createRef();

    useEffect(() => {
        refreshData();
    }, []);

    const refreshData = async () => {
        const { response: logs } = await Api.serviceLogs();
        notifierRef?.current?.showSuccess("הרשימה עודכנה");
        setLogs(logs);
    }

    const handleShowDetails = (run) => {
        setCurrentLogDetails(run);
        setShowDetails(true);
    };

    const closeDetails = () => {
        setShowDetails(false);
    };

    const deleteItem = async (runId) => {
        if (!window.confirm("למחוק את הריצה?")) {
            return;
        }
        try {
            const response = await Api.deleteLog(runId);
            if (response.status === 200) {
                notifierRef?.current?.showSuccess("נמחק בהצלחה");
                setShowDetails(false);
                refreshData();
            } else {
                notifierRef?.current?.showError("שגיאה במחיקה");
            }
        } catch (error) {
            console.log(`Error deleting run ${runId}`, error);
            notifierRef?.current?.showError("שגיאה במחיקה");
        }
    };

    const runAutomation = async () => {
        try {
            const response = await Api.runAutomation();
            if (response.status === 200) {
                notifierRef?.current?.showSuccess(`${response.response.taskId} - אוטומציה רצה`);
                setTimeout(() => {
                    refreshData();
                }, 2000);
                refreshData();
            } else {
                notifierRef?.current?.showError("שגיאה בהרצת אוטומציה");
            }
        } catch (error) {
            console.log("Error running automation", error);
            notifierRef?.current?.showError("שגיאה בהרצת אוטומציה");
        }
    };

    const cardStyle = {
        background: "white",
        boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
        borderRadius: "10px",
        width: isMobile ? "95%" : "50%",
        margin: "auto"
    };

    const cellStyle = { whiteSpace: "nowrap", verticalAlign: "middle" };

    return <div>
        <div style={{
            width: "50%",
            margin: "auto",
            direction: "rtl",
            marginBottom: "10px"
        }}>
            <Button onClick={() => runAutomation()}>הרץ אוטומציה</Button>
            <Button style={{ marginRight: "10px" }} onClick={() => refreshData()}>רענן</Button>
        </div>
        <div style={cardStyle}>
            <Table hover style={{ width: isMobile ? "100%" : "90%", margin: "auto" }}>
                <thead>
                    <tr>
                        <th>Start</th>
                        <th>Status</th>
                        <th>Duration</th>
                        <th style={{ width: "45px" }}></th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((run) => {
                        const tasks = run.tasks || [];
                        const succeeded = tasks.filter((task) => task.status === "SUCCESS").length;
                        const isRunning = run.status === "Running";

                        let icon = <FaCheckCircle color="#2e7d32" />;
                        if (isRunning) {
                            icon = <FaHourglassHalf color="#6c757d" />;
                        } else if (succeeded < tasks.length) {
                            icon = <FaExclamationTriangle color="#ef6c00" />;
                        }

                        return (
                            <tr key={run.runId} onClick={() => handleShowDetails(run)} style={{ cursor: "pointer" }}>
                                <td style={cellStyle}><span dir="ltr">{moment(run.startTime).format("DD/MM HH:mm")}</span></td>
                                <td style={cellStyle}>
                                    {icon} <span dir="ltr">{succeeded} / {tasks.length}</span>
                                </td>
                                <td style={cellStyle}>{isRunning || !tasks.length ? "…" : runDuration(tasks)}</td>
                                <td style={cellStyle}>
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteItem(run.runId);
                                        }}
                                        style={{ display: "flex", justifyContent: "center" }}
                                    >
                                        <img src={remove_button} style={{ width: "24px", height: "24px" }} alt="מחק" />
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <Modal show={showDetails} onHide={closeDetails}>
                <Modal.Header closeButton>
                    <Modal.Title>Log details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Accordion defaultActiveKey="0">
                        {
                            currentLogDetails?.tasks?.map((task, index) => {
                                return <Accordion.Item key={index} eventKey={index}>
                                    <Accordion.Header>
                                        <p style={{ fontWeight: "bold" }}> {task.task}: {task.status} [{(task.duration / 60).toFixed(2)} Minutes]</p>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <p
                                            dangerouslySetInnerHTML={{
                                                __html: task.details?.replace(/\n/g, "<br>")
                                            }}
                                        />
                                        {task.screenshot &&
                                            <img width={500} height={500} src={`data:image/jpeg;base64,${task.screenshot}`}
                                                alt="Service Log Screenshot"
                                            />}

                                    </Accordion.Body>
                                </Accordion.Item>
                            })
                        }
                    </Accordion>

                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => deleteItem(currentLogDetails.runId)}>מחק</Button>
                    <Button onClick={closeDetails}>Close</Button>
                </Modal.Footer>
            </Modal>
            <Notifier ref={notifierRef} />
        </div>
    </div>
}
